import Dexie from 'dexie';

/**
 * 通过主键查询记录
 * @param {Object} table
 * @param {Number | Array} primaryKey
 * @returns {Promise<Object>}
 */
async function queryByPrimaryKey(table, primaryKey){
  if (!primaryKey){
    console.error('[queryByPrimaryKey]:primaryKey is null');
    return;
  }
  if (Array.isArray(primaryKey)){
    return await table.bulkGet(primaryKey);
  }
  return await table.get(primaryKey);
}

/**
 * 根据条件查询单条记录
 * @param {Object} table
 * @param {Object} queryOption ({conditions: [{ key: 'issueTypeId', value: 1233, operator: 'in'}, { key: 'summary', value: 'asss', operator: 'like'}], orderBy:{{key: "createTime", desc: true}}})
 * @param {Object} options
 * @returns
 */
async function queryOne(table, queryOption){
  return await queryCollection(table, queryOption)?.first();
}

/**
 * 根据条件查询多条记录
 * @param {Object} table
 * @param {Object} queryOption
 * @param {Object} options
 * @returns
 */
async function queryList(table, queryOption){
  return await queryCollection(table, queryOption)?.toArray();
}

/**
 *
 * @param {Object} table
 * @param {Object} queryOption
 * @returns
 */
async function queryListByPage(table, queryOption = {}){
  const { page } = queryOption;
  let pageNumber = page?.pageNumber || 1;
  const pageSize = page?.pageSize || 50;
  const collection = queryCollection(table, queryOption);
  const total = await collection.count();
  const maxPageNumber = Math.ceil(total / pageSize);
  if (pageNumber > maxPageNumber){
    pageNumber = maxPageNumber;
  }
  if (pageNumber < 1){
    pageNumber = 1;
  }
  const offset = (pageNumber - 1) * pageSize;
  const hasNextPage = total > pageNumber * pageSize;
  const hasPreviousPage = pageNumber > 1;

  const list = await collection.offset(offset).limit(pageSize).toArray();
  return {
    list,
    hasNextPage,
    hasPreviousPage,
    pageSize,
    pageNumber,
    total
  };
}

/**
 * 通过条件查询相关集合
 * @param {Object} table
 * @param {Object} queryOption
 * @returns {Dexie.Collection}
 */
function queryCollection(table, queryOption){
  const { conditions: queryConditions, orderBy } = queryOption;
  // orderBy和where不兼容
  if (orderBy){
    const { key, desc } = orderBy;
    if (key){
      let orderByCollection = table.orderBy(key);
      if (desc){
        orderByCollection = orderByCollection.reverse();
      }
      if (!queryConditions?.length){
        return orderByCollection;
      }
      return orderByCollection.and(filterCondition(queryConditions));
    }
  }
  //没有条件，则查询所有记录
  if (!queryConditions?.length){
    return table.toCollection();
  }

  let collection = null;
  let remainingCondition = queryConditions;

  const indexKey = chooseIndex(table, queryConditions);
  //没有可选择的索引
  if (!indexKey){
    collection = table.toCollection();
  } else {
    const { value, operator } = queryConditions.find(item => item.key === indexKey);
    if (operator === 'between'){
      collection = table.where(indexKey).between(value);
    }
    if (operator === 'in'){
      collection = table.where(indexKey).anyOf(value);
    }
    if (operator === 'equal'){
      collection = table.where(indexKey).equals(value);
    }
    remainingCondition = queryConditions.filter(item => item.key !== indexKey);
  }
  if (!remainingCondition.length){
    return collection;
  }
  return collection.and(filterCondition(remainingCondition));
}

function getTableIndexs(table){
  const schema = table.schema;
  if (!schema){
    return [];
  }
  return [schema.primKey, ...schema.indexes];
}

const operatorFilter = {
  'like': (value, queryValue) => value.includes(queryValue),
  'in': (value, queryValue) => value.includes(queryValue),
  'between': (value, queryValue) => value >= queryValue[0] && value <= queryValue[1],
  'equal': (value, queryValue) => value === queryValue,
  'default': (value, queryValue) => value === queryValue
};

function filterCondition(conditions){
  return item => conditions.every(({ key, value, operator }) => operatorFilter[operator || 'default'](item[key], value));
}

function chooseIndex(table, queryConditions) {
  const canWhereKeyOperators = ['equal', 'in', 'between'];
  const tableIndexs = getTableIndexs(table);
  const queryIndexKeyConditions = queryConditions.filter(condition => canWhereKeyOperators.includes(condition.operator) && tableIndexs.some(item => item.name === condition.key));

  if (!queryIndexKeyConditions.length){
    return;
  }

  const queryIndexes = queryIndexKeyConditions.map(item => item.key);

  const uniqueFieldIndex = tableIndexs.find(item => item.unique === true && queryIndexes.includes(item.name));
  if (uniqueFieldIndex){
    return uniqueFieldIndex.name;
  }

  const compoundIndex = tableIndexs.find(item => item.compound === true && queryIndexes.includes(item.name));
  if (compoundIndex){
    return compoundIndex.name;
  }

  return queryIndexKeyConditions[0].key;
}


async function handleError(options){
  if (options?.errorCallback){
    return await options.errorCallback();
  }
}

function iDBAddon(db){
  db.Table.prototype.queryByPrimaryKey = async function (primaryKey, options){
    try {
      return await queryByPrimaryKey(this, primaryKey);
    } catch (error) {
      console.error(error);
      return await handleError(options);
    }
  };

  db.Table.prototype.queryOne = async function (queryOption, options){
    try {
      return await queryOne(this, queryOption);
    } catch (error) {
      console.error(error);
      return await handleError(options);
    }
  };

  db.Table.prototype.queryList = async function (queryOption, options){
    try {
      return await queryList(this, queryOption);
    } catch (error) {
      console.error(error);
      return await handleError(options);
    }
  };

  db.Table.prototype.queryListByPage = async function (queryOption, options){
    try {
      return await queryListByPage(this, queryOption);
    } catch (error) {
      console.error(error);
      return await handleError(options);
    }
  };
}

Dexie.addons.push(iDBAddon);
